/**
 * Renders a single reply within a comment. It can:
 *   - Display the text (or an edit form if "edit mode")
 *   - Show the user label of the reply's author
 *   - Show a "..." menu with "Edit" etc.
 *   - Show a date/time label
 *
 * Note that we do NOT handle "show older replies" or "reply to reply" here,
 * to keep replies simpler. That logic remains in the parent Comment component.
 */

import { useContext, useMemo } from 'react';
import { DeleteRegular, EditRegular } from '@fluentui/react-icons';
import { makeStyles, tokens } from '@fluentui/react-components';

import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { SignInContext, UserLabel, useUsers } from '@modules/users';

import { TConfirmDialogState } from '@shared/components';

import CommentCardMoreButton from '../buttons/MoreButton';
import CommentForm from '../form/CommentForm';
import DateTimeLabel from '../DateTimeLabel';

import { useEditCommentOrReply } from '../../hooks/useEditCommentOrReply.hook';
import { renderMentions } from '../../helpers/renderMentions.helper';
import { MORE_BUTTON_OPTION_KEYS, MORE_BUTTON_OPTION_LABELS } from '../../constants';
import { TReplyComment } from '../../types';
import { useArchive } from '../../api';
import { TInlineBlock } from '@modules/inlineBlocks';

type TProps = {
  reply: TReplyComment;
  item: TDocument | TFile | TInlineBlock;
  commentId: string;
  setConfirmDialogState: (state: TConfirmDialogState | undefined) => void;
};

const useStyles = makeStyles({
  container: {
    marginLeft: '1rem',
  },
  userName: {
    color: tokens.colorBrandForeground1,
  },
});

const Reply = ({ reply, item, commentId, setConfirmDialogState }: TProps) => {
  const { user } = useContext(SignInContext);
  const { userMap } = useUsers();

  // Extract the most recent content
  const content = reply.contentVersions[reply.contentVersions.length - 1];

  const { isEditing, setIsEditing, editForm, handleEditSubmit } = useEditCommentOrReply({
    itemId: item._id,
    itemType: item.type,
    commentId,
    replyId: reply._id,
    initialContent: content,
  });

  // -------------- "Archive" logic for replies --------------
  const archiveReply = useArchive();

  const handleArchiveReply = () => {
    const openDialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Delete reply?`,
      content: 'Are you sure you want to delete this reply?',
      buttonLabel: 'Delete',
      onConfirm: () => {
        archiveReply.mutate({
          itemId: item._id,
          itemType: item.type,
          commentId,
          replyId: reply._id,
        });
        setConfirmDialogState({ ...openDialogState, isOpen: false });
      },
    };

    setConfirmDialogState(openDialogState);
  };

  // -------------- JSX-related logic --------------
  const classes = useStyles();

  const displayedContent = useMemo(() => renderMentions(content, userMap, classes.userName), [content, userMap]);

  // Check if current user is the owner of this reply
  const isOwner = user._id === reply.creator;

  // Build the "more" menu items
  const moreButtonOptions = useMemo(
    () => [
      {
        key: MORE_BUTTON_OPTION_KEYS.EDIT_REPLY,
        label: MORE_BUTTON_OPTION_LABELS.EDIT_REPLY,
        onClick: () => setIsEditing(true),
        hidden: !isOwner,
        icon: EditRegular,
      },
      {
        key: MORE_BUTTON_OPTION_KEYS.ARCHIVE_REPLY,
        label: MORE_BUTTON_OPTION_LABELS.ARCHIVE_REPLY,
        onClick: handleArchiveReply,
        hidden: !isOwner,
        icon: DeleteRegular,
      },
    ],
    [isOwner]
  );

  return (
    <div className={classes.container}>
      {/* Header row: user label and “...” button */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <UserLabel id={reply.creator} textProps={{ weight: 'semibold' }} avatarProps={{}} />
        <CommentCardMoreButton items={moreButtonOptions} />
      </div>

      {/* Either an edit form or read-only text */}
      {isEditing ? (
        <CommentForm
          form={editForm}
          isValid
          onSubmit={editForm.handleSubmit(handleEditSubmit)}
          onDismiss={() => {
            setIsEditing(false);
            editForm.reset({ text: content });
          }}
        />
      ) : (
        <p className="relative w-full break-words whitespace-pre-wrap">{displayedContent}</p>
      )}

      {/* Show createdDate vs. editedDate */}
      <DateTimeLabel createdDate={reply.createdDate} editedDate={reply.editedDate} />
    </div>
  );
};

export default Reply;
